import 'photoswipe/dist/photoswipe.css'
import 'photoswipe/dist/default-skin/default-skin.css'

import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import { Gallery as PhotoSwipe } from 'react-photoswipe-gallery'

import { Image, Props as ImageProps } from './Image'

export interface Props {
  images?: ImageProps[]
}

export const Gallery = memo(function Gallery({ images }: Props) {
  if (!images) {
    return null
  }

  return (
    <Container row space="between" wrap tag="section">
      <PhotoSwipe
        options={{
          barsSize: {
            top: 0,
            bottom: 0,
          },
          closeElClasses: '',
          closeOnScroll: false,
          closeOnVerticalDrag: false,
          pinchToClose: false,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          preloaderEl: false,
          mouseUsed: true,
          tapToToggleControls: false,
          history: false,
          showHideOpacity: true,
          maxSpreadZoom: 1,
        }}
      >
        {images.map((item, index) => (
          <Image
            key={index}
            imagesNumber={images.length}
            isLast={images.length > 4 && index === 3}
            {...item}
          />
        ))}
      </PhotoSwipe>
    </Container>
  )
})

const Container = styled(FlexBox)`
  max-width: 73.5rem;
  margin: 7.5rem auto 9.5rem;
  padding: 0 1.5rem;

  @media (max-width: 1199px) {
    margin: 5.25rem auto 7.5rem;
  }

  @media (max-width: 767px) {
    margin: 5.25rem 0;
  }

  /* OVERRIDE PHOTOSWIPE STYLE */
  .pswp__bg {
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
  }
  .pswp__img--placeholder--blank {
    background: none;
  }
  .pswp--touch {
    .pswp__button--arrow--left,
    .pswp__button--arrow--right {
      visibility: visible;
    }
  }
  .pswp__caption {
    display: flex;
    min-height: 0;
    background: none;
    .pswp__caption__center {
      max-width: none;
      background: ${({ theme }) => theme.colors.variants.neutralLight4};
      color: ${({ theme }) => theme.colors.variants.neutralDark3};
      font-family: ${({ theme }) => theme.fontFamily.paragraph};
      font-size: 0.8125rem;
      padding: 0.375rem 1.5rem;
      text-align: center;
    }
  }
  .pswp__top-bar {
    height: auto;
    background: none;
  }
  .pswp__button {
    width: 40px;
    height: 40px;
    background-color: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 1;
    &.pswp__button--arrow--left,
    &.pswp__button--arrow--right {
      margin-top: 0;
      transform: translateY(-50%);
      &:before {
        background-color: transparent;
        width: 40px;
        height: 40px;
        top: 0;
        left: 0;
      }
    }
    &.pswp__button--arrow--left {
      &:before {
        background-position: -135px -40px;
      }
    }
    &.pswp__button--arrow--right {
      &:before {
        background-position: -90px -40px;
      }
    }
  }
  .pswp__button--close {
    background-position-y: -47px;
  }
  .pswp__counter {
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    color: ${({ theme }) => theme.colors.variants.neutralLight4};
    font-family: ${({ theme }) => theme.fontFamily.paragraph};
    font-size: 0.8125rem;
    opacity: 1;
  }

  @media (-webkit-min-device-pixel-ratio: 1.1),
    (-webkit-min-device-pixel-ratio: 1.09375),
    (min-resolution: 1.1dppx),
    (min-resolution: 105dpi) {
    .pswp--svg .pswp__button--close:before,
    .pswp--svg .pswp__button--arrow--left:before,
    .pswp--svg .pswp__button--arrow--right:before {
      background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQgAAABYCAQAAACjBqE3AAAB6klEQVR4Ae3bsWpUQRTG8YkkanwCa7GzVotsI/gEgk9h4Vu4ySLYmMYgbJrc3lrwZbJwC0FMt4j7F6Y4oIZrsXtgxvx/1c0ufEX4cnbmLCmSJEmSJEmSJEmSJP3XCBPvbJU+8doWmDFwyZpLBmYlNJebz0KwzykwsuSYJSNwykEJreV2BaBMaLIQZ2xYcFgqDlmw4ayE/FwL0dDk4Qh4W37DAjgqIT+3HRbigjH+iikVdxgZStgyN0Su2sXIeTwTT+esdpcbIlfNAuZ/TxresG4zV8kYWSZNiKUTokMMSWeIwTNEn4fK2TW3gRNgVkJLuVksROA9G+bEvoATNlBCa7nZXEwdxEZxzpKRKFh+bsv8LmPFmhX1OwfIz81jIRJQ5eeqG9B+riRJkiRJkiRJkiRJkiRJkiRJUkvA/8RQoEpKlJWINFkJ62AlrEP/mNBibnv2yz/A3t7Uq3LcpoxP8COjC1T5vxoAD5VdoEqdDrd5QuW1swtUSaueh3zkiuBiqgtA2OlkeMcP/uDqugsJdbjHF65VdPMKwS0+WQc/MgKvrIOHysB9vgPwk8+85hmPbnQdvHZyDMAFD7L3EOpgMcVdvnHFS0/vlatrXvCVx0U9gt3fxvnA0/hB4nmRJEmSJEmSJEmSJGmHfgFLaDPoMu5xWwAAAABJRU5ErkJggg==) !important;
    }
  }
`
